import { Controller } from "@hotwired/stimulus";
import { heroicon } from "../../../lib/heroicon";
import eyeSvg from "heroicons/16/solid/eye.svg?raw";
import eyeSlashSvg from "heroicons/16/solid/eye-slash.svg?raw";
import trashSvg from "heroicons/16/solid/trash.svg?raw";
import textSvg from "heroicons/16/solid/bars-3-bottom-left.svg?raw";
import photoSvg from "heroicons/16/solid/photo.svg?raw";
import squareSvg from "heroicons/16/solid/stop.svg?raw";

const EYE_SVGS = { eye: eyeSvg, "eye-slash": eyeSlashSvg };

// Trois signes suffisent : le texte, l'image, et un carré plein pour tout le reste (formes,
// blocs, groupes) — le nom dit le détail.
const KIND_SVGS = { text: textSvg, image: photoSvg };

const byRole = (el, role) => el.querySelector(`[data-role="${role}"]`);

/**
 * Une ligne de la piste Calques : le nom du calque à gauche, sa barre à droite.
 *
 * Le contrôleur tracks clone le template, pose les data-*-value et l'ajoute — celui-ci suit
 * les valeurs et tient le DOM : positions de la barre, états par attributs (jamais de classe).
 */
export default class extends Controller {
  static values = {
    left: String,
    width: String,
    selected: Boolean,
    kind: String,
    label: String,
    hidden: Boolean,
    eyeIcon: String,
    nested: Boolean,
    marks: Array,
  };

  get bar() { return byRole(this.element, "bar"); }

  leftValueChanged(v) { this.bar.style.left = v; }
  widthValueChanged(v) { this.bar.style.width = v; }

  selectedValueChanged(v) {
    this.element.toggleAttribute("data-selected", v);
  }

  kindValueChanged(v) {
    const container = byRole(this.element, "glyph");
    if (container && v) container.replaceChildren(heroicon(KIND_SVGS[v] || squareSvg, "size-3.5"));
  }

  labelValueChanged(v) {
    const el = byRole(this.element, "label");
    if (el) el.textContent = v;
  }

  hiddenValueChanged(v) {
    this.element.toggleAttribute("data-hidden", v);
    byRole(this.element, "label")?.toggleAttribute("data-hidden-ol", v);
  }

  // Un enfant de conteneur : la marque d'imbrication et le retrait disent à qui il
  // appartient — il s'édite comme les autres, dans la fenêtre de son parent.
  nestedValueChanged(v) {
    this.element.toggleAttribute("data-nested", v);
  }



  eyeIconValueChanged(v) {
    const container = byRole(this.element, "eye-icon");
    if (!container || !v) return;
    container.replaceChildren(heroicon(EYE_SVGS[v], "size-3.5"));
  }

  // Les marques d'animation : une par côté, sur la largeur (en % de la barre) de la plus longue.
  marksValueChanged(marks) {
    this.element.querySelectorAll('[data-role="anim-mark"]').forEach((el) => {
      const mark = marks.find((m) => m.side === el.dataset.side);
      el.hidden = !mark;
      if (mark) el.style.width = `${mark.width}%`;
    });
    // Le nom se loge entre les zones : son retrait suit leur largeur (au moins celle d'une zone,
    // poignée et rond « Animer » compris : 40 px, plus un peu d'air).
    const name = byRole(this.element, "name");
    if (!name) return;
    const inset = (side) => {
      const mark = marks.find((m) => m.side === side);
      return mark ? `max(calc(${mark.width}% + 0.25rem), 2.75rem)` : "";
    };
    name.style.paddingLeft = inset("in");
    name.style.paddingRight = inset("out");
  }

  // La corbeille est fixe : peinte une fois au montage de la ligne, contrairement à l'œil
  // dont l'icône suit l'état.
  connect() {
    const container = byRole(this.element, "trash-icon");
    if (container && !container.firstElementChild) {
      container.replaceChildren(heroicon(trashSvg, "size-3.5"));
    }
  }
}
